import React, { useState } from 'react';
import { X, Upload, CheckCircle } from 'lucide-react';

const ApplicationModal = ({ isOpen, onClose, jobTitle }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [formError, setFormError] = useState("");

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setIsSubmitting(true);
    
    try {
      const form = e.target;
      const email = form.email.value;
      const phone = form.phone.value;

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setFormError("Please enter a valid email address.");
        setIsSubmitting(false);
        return;
      }

      const phoneRegex = /^\+?[\d\s\-()]{7,20}$/;
      if (!phoneRegex.test(phone)) {
        setFormError("Please enter a valid phone number (e.g. +237 670 00 00 00).");
        setIsSubmitting(false);
        return;
      }

      // Map our beautiful UI fields to Jotform's expected fields
      const formData = new FormData();
      formData.append("formID", "261903744332051");
      
      const nameParts = form.name.value.trim().split(" ");
      formData.append("q2_q2_fullname0[first]", nameParts[0] || "");
      formData.append("q2_q2_fullname0[last]", nameParts.slice(1).join(" ") || "Applicant");
      
      formData.append("q3_q3_email1", email);
      formData.append("q4_q4_phone2[full]", phone);
      formData.append("q5_q5_textbox3", form.address.value);
      
      // Jotform uses a textarea for Cover Letter.
      if (form.coverLetter.value) {
        formData.append("q7_q7_textarea5", `Job Role: ${jobTitle}\n\nCover Letter:\n${form.coverLetter.value}`);
      } else {
        formData.append("q7_q7_textarea5", `Job Role: ${jobTitle}`);
      }

      // Jotform allows multiple files in the resume field array, but we only have one now
      if (resumeFile) {
        formData.append("q6_q6_fileupload4[]", resumeFile);
      }
      
      // Jotform spam protection hidden field
      formData.append("simple_spc", "261903744332051-261903744332051");

      // Submit silently to Jotform
      await fetch("https://submit.jotform.com/submit/261903744332051", {
        method: "POST",
        body: formData,
        mode: "no-cors"
      });
      
      setIsSuccess(true);
    } catch (error) {
      console.error(error);
      setFormError("Error submitting the application. Please check your connection.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      <div className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-slate-50/50">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Apply for this Role</h2>
            <p className="text-slate-500 mt-1">{jobTitle}</p>
          </div>
          <button 
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-slate-200 hover:text-slate-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto custom-scrollbar">
          {isSuccess ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-6">
                <CheckCircle className="w-10 h-10" />
              </div>
              <h3 className="text-3xl font-bold text-slate-900 mb-4">Application Submitted!</h3>
              <p className="text-slate-600 max-w-md mx-auto mb-8 text-lg">
                Thank you for applying to Cirronyx. We have received your application for <strong>{jobTitle}</strong> and will review it shortly.
              </p>
              <button 
                onClick={onClose}
                className="bg-slate-900 text-white px-8 py-3 rounded-xl font-semibold hover:bg-slate-800 transition"
              >
                Close Window
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">Full Name *</label>
                  <input type="text" id="name" name="name" required className="text-slate-900 w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600 transition" placeholder="John Doe" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">Email Address *</label>
                  <input type="email" id="email" name="email" required className="text-slate-900 w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600 transition" placeholder="john@example.com" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-2">Phone Number *</label>
                  <input type="tel" id="phone" name="phone" required className="text-slate-900 w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600 transition" placeholder="+237 670 00 00 00" />
                </div>
                <div>
                  <label htmlFor="address" className="block text-sm font-semibold text-slate-700 mb-2">City & Country *</label>
                  <input type="text" id="address" name="address" required className="text-slate-900 w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600 transition" placeholder="Yaoundé, Cameroon" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">CV / Resume (Required) *</label>
                  <div className="relative">
                    {resumeFile ? (
                      <div className="w-full px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl text-blue-700 flex items-center justify-between">
                        <div className="flex items-center gap-3 truncate">
                          <Upload className="w-5 h-5 flex-shrink-0" />
                          <span className="text-sm font-medium truncate">{resumeFile.name}</span>
                        </div>
                        <button 
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setResumeFile(null);
                            const input = document.querySelector('input[name="file"]');
                            if (input) input.value = '';
                          }}
                          className="p-1 hover:bg-blue-100 rounded-full transition-colors flex-shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-full px-4 py-3 bg-slate-50 border border-dashed border-slate-300 rounded-xl text-slate-500 flex items-center justify-center gap-3 hover:bg-slate-100 transition-colors cursor-pointer">
                        <Upload className="w-5 h-5" />
                        <span className="text-sm font-medium">Click to Upload PDF or DOCX</span>
                      </div>
                    )}
                    <input 
                      type="file" 
                      name="file" 
                      required={!resumeFile} 
                      accept=".pdf,.doc,.docx" 
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          setResumeFile(e.target.files[0]);
                        }
                      }}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" 
                      style={{ display: resumeFile ? 'none' : 'block' }}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="coverLetter" className="block text-sm font-semibold text-slate-700 mb-2">Cover Letter</label>
                <textarea id="coverLetter" name="coverLetter" rows={5} className="text-slate-900 w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600 transition resize-none" placeholder="Write your cover letter here..."></textarea>
              </div>

              {formError && (
                <div className="text-red-600 text-sm font-medium bg-red-50 p-4 rounded-xl border border-red-100">
                  {formError}
                </div>
              )}

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-4">
                <button 
                  type="button" 
                  onClick={onClose}
                  className="px-6 py-3 font-semibold text-slate-600 hover:text-slate-900 transition"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className={`bg-blue-600 text-white px-8 py-3 rounded-xl font-bold transition flex items-center gap-2 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-blue-700'}`}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationModal;
